import emojiDataSource from 'emoji-datasource/emoji.json';

import {
  EmojiDataSourceItem,
  EmojiGroup,
  EmojiItem,
  EmojiSkinVariation,
  SkinColour,
} from './types';

export enum EmojiCategory {
  SmileysEmotion = 'app.emoji.category.smileys_emotion',
  PeopleBody = 'app.emoji.category.people_body',
  AnimalsNature = 'app.emoji.category.animals_nature',
  FoodDrink = 'app.emoji.category.food_drink',
  TravelPlaces = 'app.emoji.category.travel_places',
  Activities = 'app.emoji.category.activities',
  Objects = 'app.emoji.category.objects',
  Symbols = 'app.emoji.category.symbols',
  Flags = 'app.emoji.category.flags',
  SkinTones = 'app.emoji.category.skin_tones',
}

const categoryMap: Record<string, EmojiCategory> = {
  'Smileys & Emotion': EmojiCategory.SmileysEmotion,
  'People & Body': EmojiCategory.PeopleBody,
  'Animals & Nature': EmojiCategory.AnimalsNature,
  'Food & Drink': EmojiCategory.FoodDrink,
  'Travel & Places': EmojiCategory.TravelPlaces,
  Activities: EmojiCategory.Activities,
  Objects: EmojiCategory.Objects,
  Symbols: EmojiCategory.Symbols,
  Flags: EmojiCategory.Flags,
  'Skin Tones': EmojiCategory.SkinTones,
};

export const unifiedToEmoji = (unicode: string): string =>
  unicode
    .split('-')
    .map(code => String.fromCodePoint(<any>`0x${code}`))
    .join('');

export const EMOJIS = (<EmojiDataSourceItem[]>emojiDataSource).reduce((acc, item) => {
  const emoji: EmojiItem = {
    unified: item.unified,
    symbol: unifiedToEmoji(item.unified),
    shortName: item.short_name,
    shortNames: item.short_names,
    category: categoryMap[item.category],
    sortOrder: item.sort_order,
    addedIn: parseFloat(item.added_in),
    hasImgTwitter: item.has_img_twitter,
    skinVariations: item.skin_variations
      ? <Record<SkinColour, EmojiSkinVariation>>Object.fromEntries(
          Object.entries(item.skin_variations).map(([key, value]) => [
            key,
            {
              unified: value.unified,
              symbol: unifiedToEmoji(value.unified),
              addedIn: parseFloat(value.added_in),
              hasImgTwitter: value.has_img_twitter,
            },
          ]),
        )
      : null,
  };

  if (emoji.skinVariations) {
    Object.values(emoji.skinVariations).forEach(variation => (variation.base = emoji));
  }

  return [...acc, emoji];
}, <EmojiItem[]>[]);

export const ALl_EMOJIS: (EmojiItem | EmojiSkinVariation)[] = EMOJIS.flatMap(emoji => [
  emoji,
  ...Object.values(emoji.skinVariations || {}),
]);

export const SYMBOL_EMOJI_MAP = <Record<string, EmojiItem>>(
  Object.fromEntries(ALl_EMOJIS.map(emoji => [emoji.symbol, emoji]))
);

export const EMOJI_SHORTCODE_MAP = <Record<string, EmojiItem>>(
  Object.fromEntries(EMOJIS.map(item => [item.shortName, item]))
);

const SKIP_CATEGORIES = Object.fromEntries([EmojiCategory.SkinTones].map(name => [name, true]));
const CATEGORY_ORDER = Object.fromEntries(
  [
    EmojiCategory.SmileysEmotion,
    EmojiCategory.PeopleBody,
    EmojiCategory.AnimalsNature,
    EmojiCategory.FoodDrink,
    EmojiCategory.TravelPlaces,
    EmojiCategory.Activities,
    EmojiCategory.Objects,
    EmojiCategory.Symbols,
    EmojiCategory.Flags,
  ].map((name, index) => [name, index]),
);

export const EMOJI_GROUPED: EmojiGroup[] = Object.entries(
  EMOJIS.reduce((acc, item) => {
    if (SKIP_CATEGORIES[item.category]) return acc;
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category] = [...acc[item.category], item];

    return acc;
  }, <Record<string, EmojiItem[]>>{}),
)
  .map(([name, list]) => ({
    name,
    list: [...list].sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1)),
  }))
  .sort((a, b) => (CATEGORY_ORDER[a.name] > CATEGORY_ORDER[b.name] ? 1 : -1));

export const FORCE_FALLBACK = ['👩‍❤️‍💋‍👨', '👨‍❤️‍💋‍👨', '👩‍❤️‍💋‍👩', '👩‍❤️‍👨', '👨‍❤️‍👨', '👩‍❤️‍👩'];
